import React, { useState, useEffect, useRef, useMemo } from 'react';
import { get_auth_key, getStoredData } from '../../../util';
import { get, post, put, destroy } from '../../../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY, units } from '../../../util/constant';
import { Table, Modal, Button, Form, FloatingLabel, Row, Col, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationInPage from '../../Pagination';
import useDebounce from '../../useDebounce';
import Company from '../../../components/company';
import Factory from '../../../components/factory';
import moment from 'moment';
import { FiTrash2 } from 'react-icons/fi';
import { GrDocumentPdf } from 'react-icons/gr';
import { EditIcon, ViewIcon } from '../../icons';
import ProductionIndentDetail from './productionIndentDetail';
import Item from '../../../components/item';
import Kitchen from '../../../components/Kitchen';
import KitchenUpdateModal from '../components/KitchenUpdateModal';
import DraftModal from '../../../components/DraftModal';
import { formatDate } from '../../../util/helper';

const ProductionIndents = () => {
  const storedData = JSON.parse(getStoredData(get_auth_key()));
  const admins = ['admin', 'super-admin', 'inventory-admin'].includes(storedData.role);

  const formFields = {
    productionIndentNumber: '',
    productionIndentDate: moment().format('YYYY-MM-DD'),
    factoryId: !admins ? storedData?.locationId[0] || '' : '',
    kitchenId: '',
    productionIndentDetails: [],
  };
  const indentFields = [{ itemId: '', unit: '', description: '', requisitionQuantity: '', issuedQuantity: '' }];
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [indentList, setIndentList] = useState([]);
  const [formDetails, setFormDetails] = useState(formFields);
  const [showModel, setShowModel] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: moment().format('YYYY-MM-DD'),
    dateTo: moment().format('YYYY-MM-DD'),
    query: '',
    factoryId: !admins ? storedData?.locationId[0] || '' : '',
    kitchenId: '',
  });
  const [factories, setFactories] = useState([]);
  const [company, setCompany] = useState(!admins ? storedData?.companyId || '' : '');
  const [filteredFactories, setFilteredFactories] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [productionIndentDetails, setProductionIndentDetails] = useState(indentFields);
  const [stockDetails, setStockDetails] = useState([]);
  const [viewDetail, setViewDetail] = useState(false);
  const [kitchenQuery, setKitchenQuery] = useState(!admins ? `factoryId=${+storedData?.locationId[0]}` : '');
  const clickSubmitBtn = useRef(null);
  const [loading, setLoading] = useState(false);
  const [updateKitchenModal, setUpdateKitchenModal] = useState(false);
  const [updateKitchenProp, setUpdateKitchenProp] = useState({
    indentId: '',
    factoryId: '',
    kitchenId: '',
  });
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [showDraftNameModal, setShowDraftNameModal] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [kitchens, setKitchens] = useState([]);
  const handleShow = async () => {
    setShowModel(true);
    setCurrentDraft(null);
    setProductionIndentDetails(indentFields);
  };

  const addIndent = () => {
    const newObject = { itemId: '', description: '', requisitionQuantity: '', issuedQuantity: '', inStockQty: '' };
    setProductionIndentDetails([...productionIndentDetails, newObject]);
  };

  const handleIndentDetailChange = (index, field, value) => {
    const updatedIndentDetails = [...productionIndentDetails];
    updatedIndentDetails[index][field] = value;
    if (field === 'itemId') {
      const foundItem = itemList.find((item) => item.id === +value);
      if (foundItem) {
        updatedIndentDetails[index].name = foundItem.name;
        updatedIndentDetails[index].unit = foundItem.unit;
        updatedIndentDetails[index].hsnCode = foundItem?.hsnCode ?? '';
      } else {
        updatedIndentDetails[index].unit = '';
        updatedIndentDetails[index].hsnCode = '';
      }
      const data = stockDetails.find((item) => item?.itemId === +value);
      if (data) {
        updatedIndentDetails[index].inStockQty = data?.quantity;
      } else {
        updatedIndentDetails[index].inStockQty = 0;
      }
    }
    setProductionIndentDetails(updatedIndentDetails);
  };

  const handleView = (e, id) => {
    e.preventDefault();
    try {
      get(`/inv/production-indent/?id=${id}`).then((res) => {
        setFormDetails(res?.data?.rows[0]);
      });
      setViewDetail(true);
    } catch (error) {
      toast.error(error?.response?.data?.data?.rows?.message || 'Something went wrong');
    }
  };

  const handleEditKitchen = (productionIndentId, factoryId, kitchenId) => {
    setUpdateKitchenProp({
      productionIndentId,
      factoryId,
      kitchenId,
    });
    setUpdateKitchenModal(true);
  };

  const handleClose = () => {
    setShowModel(false);
    clearForm();
  };

  const handleChange = (event) => {
    event.preventDefault();
    const { name, value } = event.target;
    let _formDetails = { ...formDetails, [name]: value };
    setFormDetails(_formDetails);
  };

  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    try {
      for (const indent of productionIndentDetails) {
        if (indent?.issuedQuantity > indent?.inStockQty) {
          toast.error("Issued quantity can't be more than available quantity.");
          setLoading(false);
          return;
        }
      }
      await post({ ...formDetails, productionIndentDetails }, '/inv/production-indent');
      setLoading(false);
      toast.success('Indent added successfully');
      setShowModel(false);
      getIndents();
      clearForm();
    } catch (err) {
      setLoading(false);
      if (err?.response?.data?.data?.message.startsWith('We already') === true) {
        makeIndentNumber();
        clickSubmitBtn.current.click();
      }
      toast.error(err.response.data.message || err.response.data.data.message || err.message, { autoClose: 2000 });
    }
  };

  const clearForm = () => {
    setCompany('');
    setFormDetails(formFields);
    setProductionIndentDetails(indentFields);
    setCurrentDraft(null);
  };

  function getIndents() {
    try {
      get(
        `/inv/production-indent/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&query=${filters.query}&dateFrom=${filters.dateFrom}&dateTo=${filters.dateTo}&factoryId=${filters.factoryId}&kitchenId=${filters.kitchenId}`
      ).then((response) => {
        setIndentList(response.data.rows);
        setTotalRecords(response.data.count ?? 0);
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getIndents();
      }
    },
    [filters],
    DEBOUNCE_DELAY
  );

  useEffect(() => {
    getIndents();
  }, [currentPage]);

  useEffect(() => {
    getFactories();
    getItems();
  }, []);

  const getItems = useMemo(() => {
    return () => {
      try {
        get(`/inv/items`).then((response) => {
          setItemList(response.data.rows);
        });
      } catch (err) {
        toast.error(err?.response?.data?.data?.message || err?.message);
      }
    };
  }, []);

  const makeIndentNumber = () => {
    get(
      `/inv/production-indent/generate-production-indent-number/?productionIndentDate=${formDetails.productionIndentDate}&factoryId=${formDetails.factoryId}`
    ).then((res) => {
      setFormDetails({ ...formDetails, productionIndentNumber: res?.data });
    });
  };
  useEffect(() => {
    if (formDetails.factoryId && formDetails.productionIndentDate && showModel) {
      makeIndentNumber();
    }
  }, [formDetails.factoryId, formDetails.productionIndentDate, showModel]);

  function getFactories() {
    try {
      get(`/branches/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&type=factory`).then((response) => {
        setFactories(response?.data?.rows);
        setFilteredFactories(response?.data?.rows);
      });
    } catch (err) {
      toast.error(err?.response?.data?.data?.message || err?.message);
    }
  }

  const handleSelect = (e) => {
    const { value } = e.target;
    setFormDetails((state) => {
      return { ...state, factoryId: value, kitchenId: '' };
    });
    setKitchenQuery(`factoryId=${+value}`);
    setProductionIndentDetails(indentFields);
    get(`/inv/stock/?factoryId=${value}`).then((res) => {
      setStockDetails(res?.data?.rows);
    });
  };
  useEffect(() => {
    if (storedData.role === 'inventory-manager' && storedData.locationId && storedData.locationId.length > 0) {
      setKitchenQuery(`factoryId=${+storedData.locationId[0]}`);
      // Set the factoryId to the first locationId
      setFormDetails((prevState) => ({
        ...prevState,
        factoryId: storedData.locationId[0],
      }));
      // Get stock for this factory
      get(`/inv/stock/?factoryId=${storedData.locationId[0]}`).then((res) => {
        setStockDetails(res?.data?.rows);
      });
    }
  }, [storedData.role === 'inventory-manager', showModel]);

  const onCompanyChange = (e) => {
    const { value, name } = e.target;
    setCompany(value);
    setFormDetails((state) => {
      return { ...state, companyId: value, factoryId: !admins ? storedData?.locationId[0] || '' : '' };
    });
    setFilteredFactories(factories.filter((item) => +item.companyId === +value));
  };

  const removeIndentDetail = (indexToRemove) => {
    const updatedIndentDetails = [...productionIndentDetails];
    updatedIndentDetails.splice(indexToRemove, 1);
    setProductionIndentDetails(updatedIndentDetails);
  };

  // Load all drafts
  const loadDrafts = async () => {
    setIsLoadingDrafts(true);
    try {
      const [draftsResponse, kitchensResponse] = await Promise.all([get('/user-draft/production_indent/drafts'), get(`/inv/kitchen`)]);

      if (draftsResponse.data && draftsResponse?.data?.data) {
        setDrafts(draftsResponse.data.data);
      }
      if (kitchensResponse.data && kitchensResponse?.data?.rows) {
        setKitchens(kitchensResponse.data.rows);
      }
    } catch (error) {
      console.error('Error loading drafts:', error);
      toast.error('Failed to load drafts');
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  // Load specific draft
  const loadDraft = async (draftId) => {
    try {
      const response = await get(`/user-draft/draft/${draftId}`);
      if (response.data && response?.data?.data) {
        const savedData = response.data.data.saveData;
        setFormDetails(savedData.formDetails);
        setProductionIndentDetails(savedData.productionIndentDetails);

        // Set company and kitchen query based on loaded data
        if (savedData.formDetails.companyId) {
          setCompany(savedData.formDetails.companyId);
          setFilteredFactories(factories.filter((item) => +item.companyId === +savedData.formDetails.companyId));
        }
        if (savedData.formDetails.factoryId) {
          setKitchenQuery(`factoryId=${+savedData.formDetails.factoryId}`);
          get(`/inv/stock/?factoryId=${savedData.formDetails.factoryId}`).then((res) => {
            setStockDetails(res?.data?.rows);
          });
        }

        setCurrentDraft(response.data.data);
        setShowDraftsModal(false);
        setShowModel(true);
        toast.success('Draft loaded successfully');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Failed to load draft');
    }
  };

  // Delete draft
  const deleteDraft = async (draftId) => {
    try {
      await destroy(`/user-draft/draft/${draftId}`);
      toast.success('Draft deleted successfully');
      loadDrafts();
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  // Save draft
  const saveDraft = async () => {
    const saveData = {
      formDetails,
      productionIndentDetails,
    };

    if (currentDraft) {
      setIsSavingDraft(true);
      try {
        await put(
          {
            saveData,
          },
          `/user-draft/draft/${currentDraft.id}`
        );
        toast.success('Draft updated successfully');
      } catch (error) {
        console.error('Error saving draft:', error);
        toast.error('Failed to save draft');
      } finally {
        setIsSavingDraft(false);
      }
    } else {
      setShowDraftNameModal(true);
    }
  };

  // Create new draft with name
  const createDraft = async () => {
    if (!draftName.trim()) {
      toast.error('Please enter a draft name');
      return;
    }

    setIsSavingDraft(true);
    try {
      const saveData = {
        formDetails,
        productionIndentDetails,
      };

      const response = await post(
        {
          saveType: 'production_indent',
          name: draftName,
          saveData,
        },
        '/user-draft/save'
      );

      if (response.data && response?.data?.data) {
        setCurrentDraft(response.data.data);
      }

      setShowDraftNameModal(false);
      setDraftName('');
      toast.success('Draft created successfully');
    } catch (error) {
      console.error('Error creating draft:', error);
      toast.error('Failed to create draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Production Indents | {totalRecords ?? 0}
          </h4>
          <div className="card-header-actions">
            <FloatingLabel label="Factory" style={{ marginRight: '20px' }}>
              <Factory
                factoryId={filters.factoryId}
                onChange={(e) => {
                  setFilters({ ...filters, factoryId: e.target.value });
                }}
                factories={factories}
                label="factory"
                name="factoryId"
                disabled={!admins}
              />
            </FloatingLabel>
            <div style={{ marginRight: '20px' }}>
              <Kitchen
                query={`factoryId=${filters.factoryId}`}
                kitchenId={filters.kitchenId}
                onChange={(kitchenId) => setFilters({ ...filters, kitchenId })}
                name={'kitchenId'}
                isDisabled={filters.factoryId === ''}
              />
            </div>
            <FloatingLabel style={{ marginRight: '20px' }} controlId="floatingInput" label="Start Date">
              <Form.Control value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} type="date" />
            </FloatingLabel>
            <FloatingLabel style={{ marginRight: '20px' }} controlId="floatingInput" label="End Date">
              <Form.Control value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} type="date" />
            </FloatingLabel>
            <Form.Control
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
              type="search"
              placeholder="Search by Indent Number"
              style={{ width: '250px', marginRight: '20px' }}
            />
            <button
              type="button"
              className="btn app-btn-secondary btn-sm me-2"
              onClick={() => {
                loadDrafts();
                setShowDraftsModal(true);
              }}
              style={{ minWidth: '80px' }}
            >
              Load Drafts
            </button>
            <button type="button" className="btn app-btn-default dark-color " onClick={handleShow}>
              Add Production Indent
            </button>
          </div>
        </div>
        <div className="card-body px-0 rowspan-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped bordered>
                <thead>
                  <tr className="text-center">
                    <th rowSpan="2">Sl No.</th>
                    <th rowSpan="2">Production Indent Number</th>
                    <th rowSpan="2">Date</th>
                    <th rowSpan="2">Created Date</th>
                    <th rowSpan="2">Factory</th>
                    <th rowSpan="2">Kitchen</th>
                    <th colSpan="3">Order Details</th>
                    <th rowSpan="2">Created By</th>
                    <th rowSpan="2">Action</th>
                  </tr>
                  <tr className="text-center">
                    <th style={{ width: '150px' }}>Item</th>
                    <th style={{ width: '150px' }}>Requisition Quantity</th>
                    <th style={{ width: '150px' }}>Issued Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {indentList.map((item, index) => {
                    const rowSpan = item?.ProductionIndentDetails.length;
                    return (
                      <>
                        <tr role="row" className="text-center" key={item?.id}>
                          <td rowSpan={rowSpan}>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                          <td rowSpan={rowSpan}>{item?.productionIndentNumber}</td>
                          <td rowSpan={rowSpan}>{formatDate(item?.productionIndentDate)}</td>
                          <td rowSpan={rowSpan}>{formatDate(item?.createdAt, true)}</td>
                          <td rowSpan={rowSpan}>{item?.Branch?.name}</td>
                          <td rowSpan={rowSpan}>
                            {item?.Kitchen?.name}
                            {'  '}
                            <EditIcon
                              onClick={() => handleEditKitchen(item?.id, item?.factoryId, item?.kitchenId)}
                              style={{ color: 'teal', cursor: 'pointer' }}
                            />
                          </td>
                          <td>{item?.ProductionIndentDetails[0]?.Item?.name}</td>
                          <td>{`${item?.ProductionIndentDetails[0]?.requisitionQuantity}`}</td>
                          <td>{item?.ProductionIndentDetails[0]?.issuedQuantity}</td>
                          <td rowSpan={rowSpan}>{item?.User?.name}</td>
                          <td className="actions" rowSpan={rowSpan}>
                            <ViewIcon
                              onClick={(e) => {
                                handleView(e, item.id);
                              }}
                              style={{ color: 'purple', cursor: 'pointer', marginRight: '10px' }}
                            />

                            <a href={`#/inv/production-indent/print/${item.id}`} target="_blank" rel="noreferrer">
                              <GrDocumentPdf className="react-icons red" title="Print view" />
                            </a>
                          </td>
                        </tr>
                        {item?.ProductionIndentDetails.map((detail, index) => {
                          if (index === 0) {
                            return null;
                          }
                          return (
                            <tr className="text-center">
                              <td>{detail.Item.name}</td>
                              <td>{detail.requisitionQuantity} </td>
                              <td>{detail?.issuedQuantity}</td>
                            </tr>
                          );
                        })}
                      </>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
      {showModel && (
        <Modal
          aria-labelledby="example-custom-modal-styling-title"
          dialogClassName="modal-60w"
          show={showModel}
          backdrop="static"
          keyboard={false}
          className="request-modal"
        >
          <Modal.Header>
            <Modal.Title>Add Production Indent</Modal.Title>
            <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
          </Modal.Header>
          <Modal.Body>
            <Form autoComplete="off">
              <h6>Production Indent Number: {formDetails.productionIndentNumber}</h6>
              <Row className="mb-3">
                <Col xs={4}>
                  <FloatingLabel controlId="floatingInput" label="Production Indent Date" className="mb-3">
                    <Form.Control
                      className="input"
                      value={formDetails.productionIndentDate}
                      name="productionIndentDate"
                      onChange={handleChange}
                      type="date"
                      placeholder="Production Indent Date"
                      required
                      disabled={!admins}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel label="Company" className="mb-3">
                    <Company name={'companyId'} company={company} onChange={onCompanyChange} label="Company" disabled={!admins} />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel label="Factory" className="mb-3">
                    <Factory
                      name={'factoryId'}
                      factoryId={formDetails.factoryId}
                      label={'Factory'}
                      onChange={handleSelect}
                      company={company}
                      factories={filteredFactories}
                      disabled={!admins}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <Kitchen
                    query={kitchenQuery}
                    kitchenId={formDetails.kitchenId}
                    onChange={(kitchenId) => setFormDetails({ ...formDetails, kitchenId })}
                    name={'kitchenId'}
                    isDisabled={formDetails.factoryId === ''}
                  />
                </Col>
              </Row>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h6 className="mb-0">Production Indent details:</h6>
                {currentDraft && !isSavingDraft && (
                  <span className="badge bg-info" style={{ fontSize: '0.75rem' }}>
                    Working on: {currentDraft.name}
                  </span>
                )}
              </div>
              {productionIndentDetails.map((indentDetail, index) => {
                return (
                  <Row key={index}>
                    <Col xs={3}>
                      <Item
                        itemId={indentDetail.itemId}
                        name="itemId"
                        onChange={(itemId) => {
                          handleIndentDetailChange(index, 'itemId', itemId);
                        }}
                        isDisabled={formDetails.factoryId === ''}
                      />
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel label="HSN Code" className="mb-3">
                        <Form.Control
                          disabled
                          className="input"
                          name="hsnCode"
                          value={indentDetail.hsnCode}
                          type="text"
                          placeholder="HSN Code"
                          autoComplete="false"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel label="Unit" className="mb-3">
                        <select
                          required
                          disabled
                          name="unit"
                          value={indentDetail.unit}
                          onChange={(e) => handleIndentDetailChange(index, 'unit', e.target.value)}
                          aria-label="Floating label select example"
                          className="form-select"
                        >
                          <option value="">Select Unit</option>
                          {units.map((item, index) => (
                            <option key={index} value={item.value}>
                              {item?.label}
                            </option>
                          ))}
                        </select>
                      </FloatingLabel>
                    </Col>
                    {/* Quantity */}
                    <Col xs={3}>
                      <FloatingLabel label="Requisition Quantity" className="mb-3">
                        <Form.Control
                          className="input"
                          name="quantity"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!isNaN(value) && value >= 0) {
                              handleIndentDetailChange(index, 'requisitionQuantity', value);
                            }
                          }}
                          value={indentDetail.requisitionQuantity}
                          type="text"
                          placeholder="Requisition Quantity"
                          required
                          autoComplete="false"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={3} className="mb-4">
                      <FloatingLabel label="Issued Quantity">
                        <Form.Control
                          className="input"
                          name="quantity"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!isNaN(value) && value >= 0) {
                              handleIndentDetailChange(index, 'issuedQuantity', value);
                            }
                          }}
                          value={indentDetail.issuedQuantity}
                          type="text"
                          placeholder="Issued Quantity"
                          required
                          autoComplete="false"
                        />
                        <span
                          className={indentDetail?.inStockQty >= 0 && indentDetail?.inStockQty !== '' ? 'text-danger' : 'd-none'}
                          style={{ fontSize: '13px', position: 'absolute' }}
                        >
                          Avl Qty {indentDetail?.inStockQty}
                        </span>
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <FloatingLabel label="Description" className="mb-3">
                        <Form.Control
                          className="input"
                          name="description"
                          onChange={(e) => handleIndentDetailChange(index, 'description', e.target.value)}
                          value={indentDetail.description}
                          type="text"
                          placeholder="Description"
                          autoComplete="false"
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={1} className="align-middle">
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {productionIndentDetails.length > 1 && (
                          <FiTrash2
                            style={{ fontSize: '20px', cursor: 'pointer' }}
                            color="red"
                            title="Remove Item"
                            className="react-icons red mt-2"
                            onClick={() => {
                              removeIndentDetail(index);
                            }}
                          />
                        )}
                      </div>
                    </Col>
                    <hr />
                  </Row>
                );
              })}
              <Row className="mt-3">
                <Col xs={12}>
                  <Button variant="secondary" size="sm" onClick={addIndent}>
                    + Add Items
                  </Button>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                    <Button type="button" variant="outline-primary" size="sm" onClick={saveDraft} disabled={isSavingDraft}>
                      {isSavingDraft ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                        </>
                      ) : (
                        'Save Draft'
                      )}
                    </Button>
                    {currentDraft && (
                      <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>
                        Draft: {currentDraft.name}
                      </span>
                    )}
                  </div>
                  <Button
                    style={{ backgroundColor: '#18c6d5' }}
                    onClick={() => {
                      setFormDetails({ ...formDetails, ProductionIndentDetails: productionIndentDetails, type: 'previewBeforeSubmit' });
                      setViewDetail(true);
                    }}
                    className="add-request-btn justify-content-center"
                  >
                    Preview
                  </Button>
                  {loading ? (
                    <Button className="add-request-btn mt-4 mb-3 justify-content-center" variant="primary" disabled>
                      <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                      Loading...
                    </Button>
                  ) : (
                    <Button
                      ref={clickSubmitBtn}
                      onClick={handleSubmit}
                      type="submit"
                      className="add-request-btn mt-4 mb-3 justify-content-center"
                      disabled={loading}
                    >
                      Create
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          </Modal.Body>
        </Modal>
      )}
      <Modal size="lg" show={viewDetail} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>Production Indent Details</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setViewDetail(false)}></button>
        </Modal.Header>
        <Modal.Body>
          <ProductionIndentDetail data={formDetails} isPrint={false} />
        </Modal.Body>
      </Modal>
      {updateKitchenModal && (
        <KitchenUpdateModal
          view={updateKitchenModal}
          setView={setUpdateKitchenModal}
          factoryId={updateKitchenProp.factoryId}
          kitchenId={updateKitchenProp.kitchenId}
          productionIndentId={updateKitchenProp.productionIndentId}
          callback={() => getIndents()}
        />
      )}

      <DraftModal
        showDraftsModal={showDraftsModal}
        setShowDraftsModal={setShowDraftsModal}
        showDraftNameModal={showDraftNameModal}
        setShowDraftNameModal={setShowDraftNameModal}
        isLoadingDrafts={isLoadingDrafts}
        drafts={drafts}
        kitchens={kitchens}
        loadDraft={loadDraft}
        deleteDraft={deleteDraft}
        draftName={draftName}
        setDraftName={setDraftName}
        createDraft={createDraft}
        isSavingDraft={isSavingDraft}
        draftType="production_indent"
      />
    </>
  );
};

export default ProductionIndents;
